import { Component, inject, DestroyRef, computed, Signal, signal } from '@angular/core';
import { ComponentsModule, SnackBarService, SnackBarAction, SimpleSnackBarRef } from '@mastechnicaegroup/carelinelive-ui';
import { AuthenticationService } from '@lib/auth/authentication.service';
import { ActivatedRoute, Router } from '@angular/router';
import { AsyncPipe, NgIf } from '@angular/common';
import { CountOpenPackagesComponent } from '../../sections/count/open-packages/count-open-packages.component';
import { CountRejectedPackagesComponent } from '../../sections/count/rejected-packages/count-rejected-packages.component';
import { HeaderComponent } from '@shared/layout/header/header.component';
import { CountOfferedPackagesComponent } from '../../sections/count/offered-packages/count-offered-packages.component';
import { CountRequestedPackagesComponent } from '../../sections/count/requested-packages/count-requested-packages.component';
import { CountNotInterestedPackagesComponent } from '../../sections/count/not-interested-packages/count-not-interested-packages.component';
import { DashboardStatsTableComponent } from '../../components/dashboard-stats-table/dashboard-stats-table.component';
import { RefreshableResult, successfulResult, toResult } from '@mastechnicaegroup/carelinelive-cdk/rx';
import { map, switchMap, filter, take } from 'rxjs';
import { RegionService } from '@lib/region/region.service';
import { DashboardTableSkeletonComponent } from '../../components/dashboard-table-skeleton/dashboard-table-skeleton.component';
import { toSignal, takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CountProviderOwnedPackagesComponent } from '../../sections/count/provider-owned-packages/count-provider-owned-packages.component';
import { CountAvailableToRequestComponent } from '../../sections/count/available-to-request/count-available-to-request.component';
import { PackageDashboardMeta } from '@lib/api/models/package';
import { DashboardSummaryTableComponent } from '../../components/dashboard-summary-table/dashboard-summary-table.component';
import { DashboardConfigurablePackageTableComponent } from '../../components/dashboard-configurable-package-table/dashboard-configurable-package-table.component';
import { postParamNamesEndDateOperator, postParamNamesEndDateValue, postParamNamesFilterResponse, postParamNamesOfferedOperator, postParamNamesOfferedValue } from '@shared/filters/filter-params';
import { DashboardConfigurablePackageResponseTableComponent } from '../../components/dashboard-configurable-package-response-table/dashboard-configurable-package-response-table.component';

@Component({
    selector: 'app-home-page',
    templateUrl: './dashboard-view-page.component.html',
    styleUrl: './dashboard-view-page.component.scss',
    standalone: true,
    imports: [
        AsyncPipe,
        ComponentsModule,
        HeaderComponent,
        CountOpenPackagesComponent,
        CountOfferedPackagesComponent,
        CountRejectedPackagesComponent,
        CountRequestedPackagesComponent,
        CountNotInterestedPackagesComponent,
        DashboardStatsTableComponent,
        DashboardTableSkeletonComponent,
        CountProviderOwnedPackagesComponent,
        CountAvailableToRequestComponent,
        NgIf,
        DashboardSummaryTableComponent,
        DashboardConfigurablePackageTableComponent,
        DashboardConfigurablePackageResponseTableComponent
    ]
})
export class DashboardViewPageComponent {
    private readonly _router = inject(Router);

    private readonly _authService = inject(AuthenticationService);
    private readonly _activatedRoute = inject(ActivatedRoute);
    private readonly _regionService = inject(RegionService);
    private readonly _snackBar = inject(SnackBarService);
    private readonly _destroyRef = inject(DestroyRef);

    protected readonly Object = Object;
    protected refreshSnackbarRef?: SimpleSnackBarRef;

    protected user = toSignal(this._authService.user$);

    protected readonly configForProviderPackages: Signal<Record<string, string | number | null>> = signal({
        // todays date in YYYY-MM-DD format
        [postParamNamesEndDateValue]: new Date().toISOString().split('T')[0],
        [postParamNamesEndDateOperator]: '>=',
        sort: '-updated_at'
    });

    protected columnsForProviderPackages = [
        { key: 'reference', value: 'Reference' },
        { key: 'commissioner', value: 'Commissioner' },
        { key: 'post_code', value: 'Postcode' },
        { key: 'accept_date', value: 'Accepted Date' },
        { key: 'comments', value: 'Comments' }
    ];

    protected readonly configForCommissionerOfferedPackages = computed(() => ({
        [postParamNamesOfferedValue]: 'true',
        [postParamNamesOfferedOperator]: 'is',
        sort: '-updated_at'
    }));

    protected columnsForCommissionerOfferedPackages = [
        { key: 'reference', value: 'Reference' },
        { key: 'provider', value: 'Provider' },
        { key: 'post_code', value: 'Postcode' },
        { key: 'offer_date', value: 'Offer Date' },
        { key: 'comments', value: 'Comments' }
        /*{ key: 'weekly_hours', value: 'Weekly Hours' },
        { key: 'carers_required', value: 'Carers Required' }*/
    ];

    protected readonly configForProviderOfferedPackages = computed(() => ({
        [postParamNamesOfferedValue]: this.user()?.provider?.id ?? '',
        [postParamNamesOfferedOperator]: 'is',
        sort: '-updated_at'
    }));

    protected columnsForProviderOfferedPackages = [
        { key: 'reference', value: 'Reference' },
        { key: 'commissioner', value: 'Commissioner' },
        { key: 'post_code', value: 'Postcode' },
        { key: 'offer_date', value: 'Offer Date' },
        { key: 'comments', value: 'Comments' },
    ];

    protected readonly configForProviderRequestedPackages = computed(() => ({
        [postParamNamesFilterResponse]: 'requested',
        sort: '-updated_at'
    }));

    protected columnsForProviderRequestedPackages = [
        { key: 'reference', value: 'Reference' },
        { key: 'commissioner', value: 'Commissioner' },
        { key: 'post_code', value: 'Postcode' },
        { key: 'updated_at', value: 'Requested Date' },
        { key: 'status', value: 'Status' },
    ];

    protected columnsForCommissionerRequestedPackages = [
        // possible keys are: reference, provider, comments, post_code, updated_at, commissioner-status
        { key: 'reference', value: 'Reference' },
        { key: 'provider', value: 'Provider' },
        { key: 'comments', value: 'Comments' },
        { key: 'updated_at', value: 'Requested Date' },
        { key: 'commissioner-status', value: 'Status' },
    ];

    protected readonly configForProviderDeclinedPackages = computed(() => ({
        [postParamNamesFilterResponse]: 'rejected',
        sort: '-updated_at'
    }));

    protected columnsForProviderDeclinedPackages = [
        { key: 'reference', value: 'Reference' },
        { key: 'commissioner', value: 'Commissioner' },
        { key: 'post_code', value: 'Postcode' },
        { key: 'updated_at', value: 'Declined Date' },
        { key: 'status', value: 'Status' },
    ];

    regions = new RefreshableResult({
        source: this._activatedRoute.params.pipe(
            switchMap(() => this._regionService.getPackagesByPriority().pipe(toResult()))
        )
    });

    regions$ = this.regions.result$.pipe(
        successfulResult()
    );

    // map the successful result into a single package list of all packages inside regions/priorities/packages
    packages$ = this.regions.result$.pipe(
        successfulResult(),
        // map each region's priorities' packages into a single array
        map(result => {
            const packages: PackageDashboardMeta[] = [];
            result.regions.forEach(region => {
                region.priorities.forEach(priority => {
                    // add the region.name and priority.name to each package
                    const prioPackages = [...priority.packages].map((pkg) => ({
                            ...pkg,
                            region_name: region.region_name ?? 'No Region',
                            priority_name: priority.priority_name ?? 'No Priority'
                        })
                    );
                    packages.push(...prioPackages);
                });
            });
            // sort packages by created_at descending
            packages.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
            return packages;
        })
    );

    readonly loading = toSignal(
        this.regions.result$.pipe(
            map(result => result.state === 'loading')
        ),
        { initialValue: true } // optional: treat as loading before the first value
    );

    protected logout(): void {
        this._authService.logout().subscribe(() => {
            this._router.navigate(['/auth/login']);
        });
    }

    protected onCountdownComplete(): void {
        this.refreshSnackbarRef?.dismiss();

        this.refreshSnackbarRef = this._snackBar.open({
            colour: 'success',
            icon: 'check',
            title: 'Packages have been updated',
            dismissable: true,
            actions: [
                {
                    label: 'Refresh',
                    id: 'refresh',
                    icon: 'refresh'
                }
            ]
        });
        if (this.refreshSnackbarRef) {
            this.refreshSnackbarRef.onAction().pipe(
                filter((value: SnackBarAction) => value.id === 'refresh'),
                // Ensure a single refresh per snackbar instance:
                take(1),
                takeUntilDestroyed(this._destroyRef)
            ).subscribe(() => {
                this.refreshSnackbarRef?.dismiss();
                this.regions.refresh();
            });
        }
    }
}
